import { hashString32 } from '../sim/rng';
import { campaignProgress, totalStars } from './economy';
import { fromBase64url, toBase64url } from './lawnCode';
import { loadSave, serializeSave } from './save/migrate';
import type { Save } from './save/schema';
import { THANK_YOU_COSMETIC } from './unlock';

/**
 * Save codes (lm-ifl.12): progress moved between the web and the app, or between devices, with
 * no server. A code is the save, as text: `lmsave1.<base64url JSON>.<checksum>`. The player copies
 * or shares it on one device and pastes it on the other.
 *
 * A code carries progress (lawns, coins, mowers, upgrades, finds, lawns built) and the online
 * player, never what belongs to the device: its settings, runs waiting to be sent, and the full
 * game, which only the store grants (a code must not be a way to unlock it).
 */

const PREFIX = 'lmsave1';

/** Non-ASCII as \uXXXX, so the JSON is one byte per character. */
const ascii = (json: string): string =>
  json.replace(/[^\x20-\x7e]/g, (c) => '\\u' + c.charCodeAt(0).toString(16).padStart(4, '0'));

const checksum = (payload: string): string => hashString32(payload).toString(36);

/** The save's code. */
export function encodeSaveCode(save: Readonly<Save>): string {
  const copy = structuredClone(save) as Save;
  // The online player travels (owner, 2026-10-24: a new phone keeps its name and boards); runs
  // waiting to be sent stay with the device that played them.
  copy.online = {
    ...(copy.online.player ? { player: copy.online.player } : {}),
    outbox: [],
    // The bonuses paid travel with the player, so a new phone does not pay them again.
    awardsPaid: copy.online.awardsPaid,
    // And the player's nicknames for their friends.
    ...(copy.online.nicknames ? { nicknames: copy.online.nicknames } : {}),
  };
  const json = ascii(serializeSave(copy));
  const payload = toBase64url([...json].map((c) => c.charCodeAt(0)));
  return `${PREFIX}.${payload}.${checksum(payload)}`;
}

/** A save code within pasted text (a shared message may carry more words), or null. */
export function findSaveCode(text: string): string | null {
  return /lmsave1\.[A-Za-z0-9_-]+\.[a-z0-9]+/.exec(text)?.[0] ?? null;
}

export type SaveCodeResult = { ok: true; save: Save } | { ok: false; problem: string };

const BAD: SaveCodeResult = { ok: false, problem: "That doesn't look like a save code." };

/** Reads a save code (migrating an older save), or says why it can't. */
export function decodeSaveCode(text: string): SaveCodeResult {
  const code = findSaveCode(text);
  if (!code) return BAD;
  const [, payload = '', sum = ''] = code.split('.');
  if (checksum(payload) !== sum) {
    return { ok: false, problem: 'That code is incomplete. Copy all of it.' };
  }
  const bytes = fromBase64url(payload);
  if (!bytes) return BAD;
  // (In slices: a big save has more bytes than a call may take as arguments.)
  let json = '';
  for (let i = 0; i < bytes.length; i += 4096) {
    json += String.fromCharCode(...bytes.slice(i, i + 4096));
  }
  const loaded = loadSave(json);
  if (loaded.status !== 'loaded') return BAD;
  return { ok: true, save: loaded.save };
}

/**
 * The save after taking in a code's progress: the code's progress with this device's settings
 * and its own full-game unlock (and the thank-you paint only where that unlock is).
 */
export function adoptSave(current: Readonly<Save>, incoming: Readonly<Save>): Save {
  const next = structuredClone(incoming) as Save;
  next.settings = structuredClone(current.settings);
  next.fullGame = current.fullGame;
  // The code's player (an older code has none: keep this device's), this device's outbox.
  const player = incoming.online.player ?? current.online.player;
  next.online = {
    ...(player ? { player: structuredClone(player) } : {}),
    outbox: structuredClone(current.online.outbox),
    awardsPaid: [
      ...new Set([...current.online.awardsPaid, ...(incoming.online.awardsPaid ?? [])]),
    ].slice(-100),
  };
  // Nicknames from both (the code's where both name the same friend).
  const nicknames = { ...current.online.nicknames, ...incoming.online.nicknames };
  if (Object.keys(nicknames).length > 0) next.online.nicknames = nicknames;
  const owned = next.cosmetics.owned.filter((c) => c !== THANK_YOU_COSMETIC);
  if (next.fullGame) owned.push(THANK_YOU_COSMETIC);
  next.cosmetics.owned = owned;
  next.cosmetics.equipped = Object.fromEntries(
    Object.entries(next.cosmetics.equipped).filter(([, id]) => owned.includes(id)),
  );
  return next;
}

/** A save at a glance, for the confirm step ("23 lawns, 41 stars, 1,250 coins"). */
export function saveSummary(save: Readonly<Save>): { lawns: number; stars: number; coins: number } {
  return { lawns: campaignProgress(save), stars: totalStars(save), coins: save.coins };
}
